import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiArrowRight, FiCalendar, FiChevronRight, FiDownload, FiLayers, FiPrinter, FiRefreshCw } from 'react-icons/fi';
import { DatePicker } from 'antd';
import viVN from 'antd/locale/vi_VN';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import * as XLSX from 'xlsx';
import useVerifyAccess from 'hooks/useVerifyAccess';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import { getSalesHierarchyReport } from 'services/retail-report.service';
import { getUnresolvedRevenueEventNotice } from 'utils/recognizedRevenueBasis';
import { ReportPageHeader, ReportPageLayout, ReportState } from './ReportComponents';
import {
  buildSalesHierarchyExportSheet,
  formatHierarchyDate,
  formatHierarchyNumber,
  getHierarchyRows,
  getHierarchyPeriodError,
  getMonthPeriod,
  getSalesHierarchyInitialFilters,
  hierarchyColumnLabel,
  hierarchyReportTitle,
  isSalesHierarchyReport,
} from './salesHierarchyReportData';
import './retailReports.scss';
import './salesHierarchyReport.scss';

const DATE_RANGE_LOCALE = {
  ...viVN.DatePicker,
  lang: { ...viVN.DatePicker.lang, ok: 'Áp dụng' },
};

const SalesHierarchyReportPage = () => {
  const location = useLocation();
  const { verifyPermission } = useVerifyAccess();
  const canExport = verifyPermission('SL_SALES_REPORT_EXPORT');
  const [portalScope, setPortalScope] = useState(getPortalScope);
  const companyId = `${portalScope.company_id || ''}`;
  const [filters, setFilters] = useState(() => ({
    ...getSalesHierarchyInitialFilters(location.search), company_id: companyId,
  }));
  const [report, setReport] = useState(null);
  const [scopeOptions, setScopeOptions] = useState(null);
  const [requestError, setRequestError] = useState(null);
  const [reload, setReload] = useState(0);
  const [dateRangeKey, setDateRangeKey] = useState(0);
  const requestSequence = useRef(0);
  const branchId = filters.company_id === companyId ? filters.business_id : '';
  const storeId = filters.company_id === companyId ? filters.store_id : '';
  const periodError = getHierarchyPeriodError(filters.date_from, filters.date_to);
  const validDates = !periodError;
  const query = useMemo(() => ({
    company_id: companyId,
    date_from: filters.date_from,
    date_to: filters.date_to,
    ...(branchId ? { business_id: branchId } : {}),
    ...(storeId ? { store_id: storeId } : {}),
  }), [companyId, filters.date_from, filters.date_to, branchId, storeId]);
  const context = JSON.stringify(query);
  const data = report?.context === context ? report.data : null;
  const error = requestError?.context === context ? requestError.message : '';
  const loading = Boolean(companyId && validDates && !data && !error);
  const options = scopeOptions?.companyId === companyId ? scopeOptions : null;
  const branches = options?.branches || [];
  const stores = (options?.stores || []).filter((store) => !branchId || `${store.business_id}` === branchId);
  const selectedBranch = branches.find((branch) => `${branch.business_id}` === branchId);
  const selectedStore = stores.find((store) => `${store.store_id}` === storeId);
  const companyName = data?.scope?.company_name || options?.companyName || portalScope.company_name || '';
  const branchName = data?.scope?.business_name || selectedBranch?.business_name || '';
  const storeName = data?.scope?.store_name || selectedStore?.store_name || '';
  const daily = filters.view === 'daily' || data?.level === 'store';
  const allStores = filters.view === 'stores' && !daily;
  const tableLevel = allStores ? 'branch' : data?.level;
  const rows = getHierarchyRows(data, daily, filters.view);
  const summary = data?.summary || {};
  const period = data?.period || query;
  const monthPeriod = getMonthPeriod(filters.date_from.slice(0, 7));
  const selectedMonth = monthPeriod?.date_from === filters.date_from && monthPeriod?.date_to === filters.date_to
    ? filters.date_from.slice(0, 7) : '';
  const dateRange = useMemo(() => [dayjs(filters.date_from).locale('vi'), dayjs(filters.date_to).locale('vi')], [filters.date_from, filters.date_to]);
  const scopeLabel = storeId ? storeName : branchId ? branchName : companyName;
  const tableTitle = hierarchyReportTitle(tableLevel, daily);
  const scopeCount = daily ? `${formatHierarchyNumber(rows.length)} ngày`
    : data?.level === 'branch' ? `${formatHierarchyNumber(summary.store_count)} cửa hàng`
      : `${formatHierarchyNumber(summary.branch_count)} chi nhánh · ${formatHierarchyNumber(summary.store_count)} cửa hàng`;

  useEffect(() => {
    const handleScopeChange = (event) => {
      const nextScope = event?.detail || getPortalScope();
      setPortalScope(nextScope);
      if (`${nextScope.company_id || ''}` !== companyId) {
        setFilters((current) => ({ ...current, company_id: `${nextScope.company_id || ''}`, business_id: '', store_id: '' }));
        setReport(null);
        setScopeOptions(null);
      }
    };
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, handleScopeChange);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, handleScopeChange);
  }, [companyId]);

  useEffect(() => {
    const sequence = ++requestSequence.current;
    const controller = new AbortController();
    setReport(null);
    setRequestError(null);
    if (!companyId || !validDates) return () => controller.abort();
    getSalesHierarchyReport(query, { signal: controller.signal })
      .then((result) => {
        if (sequence !== requestSequence.current || controller.signal.aborted) return;
        if (!isSalesHierarchyReport(result, companyId)) throw new Error('Dữ liệu báo cáo chưa đầy đủ. Vui lòng tải lại.');
        setScopeOptions({ companyId, companyName: result?.scope?.company_name || '', branches: result?.branches || [], stores: result?.stores || [] });
        setReport({ context, data: result });
      })
      .catch((errorValue) => {
        if (sequence !== requestSequence.current || controller.signal.aborted) return;
        setRequestError({ context, message: errorValue?.message || 'Không tải được báo cáo bán hàng.' });
      });
    return () => controller.abort();
  }, [companyId, context, query, validDates, reload]);

  useEffect(() => {
    const beforePrint = () => document.body.classList.add('bc_sh_printing');
    const afterPrint = () => document.body.classList.remove('bc_sh_printing');
    window.addEventListener('beforeprint', beforePrint);
    window.addEventListener('afterprint', afterPrint);
    return () => {
      afterPrint();
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, []);

  const refresh = useCallback(() => {
    setReport(null);
    setRequestError(null);
    setReload((current) => current + 1);
  }, []);

  const selectScope = (businessId = '', nextStoreId = '') => setFilters((current) => ({
    ...current, company_id: companyId, business_id: businessId, store_id: nextStoreId,
  }));

  const openRow = (row) => {
    if (daily || data?.level === 'store' || row.is_unallocated) return;
    if (allStores) selectScope(`${row.business_id}`, `${row.store_id}`);
    else if (data?.level === 'company') selectScope(`${row.business_id}`, '');
    else selectScope(`${row.business_id || branchId}`, `${row.store_id}`);
  };

  const exportExcel = () => {
    if (!canExport || !data || loading || error) return;
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, buildSalesHierarchyExportSheet(data, daily, filters.view), 'Báo cáo bán hàng');
    const scopeSuffix = storeId ? `cua-hang-${storeId}` : branchId ? `chi-nhanh-${branchId}` : `cong-ty-${companyId}`;
    XLSX.writeFile(workbook, `ban-hang-${scopeSuffix}-${period.date_from}-${period.date_to}${daily ? '-theo-ngay' : allStores ? '-theo-cua-hang' : ''}.xlsx`);
  };

  return (
    <ReportPageLayout reportId='sales-hierarchy'>
      <ReportPageHeader
        title='Bán hàng theo chi nhánh'
        description='Xem toàn bộ cửa hàng của công ty, chọn chi nhánh hoặc cửa hàng để xem chi tiết. Chọn tháng để tổng hợp doanh thu theo ngày.'
        actions={(
          <>
            <button type='button' className='bc_rr_action_button' onClick={refresh} disabled={loading || !companyId || !validDates}>
              <FiRefreshCw aria-hidden='true' /> Làm mới
            </button>
            <button type='button' className='bc_rr_action_button' onClick={() => window.print()} disabled={!data || loading || !!error}>
              <FiPrinter aria-hidden='true' /> In báo cáo
            </button>
            {canExport ? <button type='button' className='bc_rr_action_button bc_sh_export' onClick={exportExcel} disabled={!data || loading || !!error}>
              <FiDownload aria-hidden='true' /> Xuất Excel
            </button> : null}
          </>
        )}
      />

      <section className='bc_sh_filters' aria-label='Bộ lọc báo cáo'>
        <div className='bc_sh_scope_fields'>
          <label><span>Tổng công ty</span><select aria-label='Tổng công ty' value={companyId} disabled>
            <option value={companyId}>{companyName || (companyId ? 'Công ty đang chọn' : 'Chưa chọn công ty')}</option>
          </select><small>Chọn công ty ở thanh trên cùng.</small></label>
          <label><span>Chi nhánh</span><select aria-label='Chi nhánh' value={branchId} disabled={!companyId || !branches.length} onChange={(event) => selectScope(event.target.value)}>
            <option value=''>Tất cả chi nhánh</option>
            {branches.map((branch) => <option key={branch.business_id} value={branch.business_id}>{branch.business_name}</option>)}
          </select></label>
          <label><span>Cửa hàng</span><select aria-label='Cửa hàng' value={storeId} disabled={!companyId || !stores.length} onChange={(event) => {
            const store = stores.find((item) => `${item.store_id}` === event.target.value);
            selectScope(store ? `${store.business_id}` : branchId, event.target.value);
          }}>
            <option value=''>Tất cả cửa hàng</option>
            {stores.map((store) => <option key={store.store_id} value={store.store_id}>{store.store_name}</option>)}
          </select></label>
        </div>
        <div className='bc_sh_date_fields'>
          <label className='bc_sh_month'><span><FiCalendar aria-hidden='true' /> Chọn tháng</span><input aria-label='Chọn tháng' type='month' value={selectedMonth} onChange={(event) => {
            const nextPeriod = getMonthPeriod(event.target.value);
            if (nextPeriod) setFilters((current) => ({ ...current, ...nextPeriod, view: 'daily' }));
          }} /><small>Hiển thị từng ngày trong tháng.</small></label>
          <div className='bc_sh_date_range' role='group' aria-labelledby='bc_sh_date_range_label'>
            <span id='bc_sh_date_range_label'><FiCalendar aria-hidden='true' /> Khoảng thời gian</span>
            <label className='bc_sh_date_input_label' htmlFor='bc_sh_date_from'>Từ ngày</label>
            <label className='bc_sh_date_input_label' htmlFor='bc_sh_date_to'>Đến ngày</label>
            <DatePicker.RangePicker
              key={dateRangeKey}
              id={{ start: 'bc_sh_date_from', end: 'bc_sh_date_to' }}
              className='bc_sh_range_picker'
              classNames={{ popup: { root: 'bc_sh_date_range_popup' } }}
              locale={DATE_RANGE_LOCALE}
              format='DD/MM/YYYY'
              placeholder={['Từ ngày', 'Đến ngày']}
              allowClear={false}
              needConfirm
              value={dateRange}
              status={validDates ? undefined : 'error'}
              aria-describedby={!validDates ? 'bc_sh_date_range_error' : undefined}
              onOpenChange={(open) => {
                if (!open) setDateRangeKey((current) => current + 1);
              }}
              onChange={(dates) => {
                if (dates?.length !== 2 || !dates[0]?.isValid() || !dates[1]?.isValid()) return;
                setFilters((current) => ({ ...current, date_from: dates[0].format('YYYY-MM-DD'), date_to: dates[1].format('YYYY-MM-DD') }));
              }}
            />
          </div>
          <div className='bc_sh_date_note'>Doanh thu thuần chưa VAT, theo ngày thực xuất kho.<br />Trả hàng theo ngày nhập trả thực tế; không gồm chuyển hàng nội bộ.<br />Đơn vị: VND.</div>
        </div>
        {!validDates ? <p id='bc_sh_date_range_error' className='bc_sh_validation' role='alert'>{periodError}</p> : null}
      </section>

      <nav className='bc_sh_scope_path' aria-label='Cấp báo cáo'>
        <button type='button' aria-current={!branchId ? 'page' : undefined} onClick={() => selectScope()}><FiLayers aria-hidden='true' /> Tổng công ty</button>
        <FiChevronRight aria-hidden='true' />
        {branchId ? <button type='button' aria-current={!storeId ? 'page' : undefined} onClick={() => selectScope(branchId)}>{branchName || 'Chi nhánh đang chọn'}</button> : <span>Chi nhánh</span>}
        <FiChevronRight aria-hidden='true' />
        {storeId ? <button type='button' aria-current='page'>{storeName || 'Cửa hàng đang chọn'}</button> : <span>Cửa hàng</span>}
      </nav>

      <div className='bc_sh_view_bar'>
        <div role='tablist' aria-label='Cách xem báo cáo'>
          <button type='button' role='tab' aria-selected={allStores} disabled={data?.level === 'store' || !!storeId} onClick={() => setFilters((current) => ({ ...current, view: 'stores' }))}><FiLayers aria-hidden='true' /> Theo cửa hàng</button>
          <button type='button' role='tab' aria-selected={!daily && !allStores} disabled={data?.level === 'store' || !!storeId} onClick={() => setFilters((current) => ({ ...current, view: 'units' }))}><FiLayers aria-hidden='true' /> Theo đơn vị</button>
          <button type='button' role='tab' aria-selected={daily} onClick={() => setFilters((current) => ({ ...current, view: 'daily' }))}><FiCalendar aria-hidden='true' /> Theo thời gian</button>
        </div>
        <span>{data ? scopeCount : 'Theo phạm vi được phân quyền'}</span>
      </div>

      {loading ? <ReportState type='loading'>Đang tổng hợp doanh thu trong phạm vi đã chọn…</ReportState> : null}
      {error ? <div className='bc_sh_error'><ReportState type='error'>{error}</ReportState><button type='button' className='bc_rr_action_button' onClick={refresh}>Thử lại</button></div> : null}
      {!companyId ? <ReportState>Chọn công ty ở thanh trên cùng để xem báo cáo.</ReportState> : null}

      {data && validDates && !error ? (
        <section className='bc_sh_report' aria-label={tableTitle}>
          <div className='bc_sh_report_heading'>
            <span className='bc_sh_report_tag'>{scopeLabel || 'Phạm vi báo cáo'}</span>
            <h3>{tableTitle}</h3>
            <p>Từ ngày {formatHierarchyDate(period.date_from)} đến ngày {formatHierarchyDate(period.date_to)}</p>
            <div><span>Tổng công ty: <strong>{companyName}</strong></span><span>Chi nhánh: <strong>{branchName || 'Tất cả'}</strong></span><span>Cửa hàng: <strong>{storeName || 'Tất cả'}</strong></span></div>
          </div>
          {getUnresolvedRevenueEventNotice(data.meta) ? <p role='alert' className='bc_sh_date_note'>{getUnresolvedRevenueEventNotice(data.meta)}</p> : null}
          {Number(data.meta?.unallocated_document_count) > 0 ? (
            <p className='bc_sh_date_note'>Trong tổng doanh thu có {formatHierarchyNumber(data.meta.unallocated_revenue)}đ chưa gán cửa hàng, được trình bày riêng trong bảng.</p>
          ) : null}
          <div className='bc_sh_table_wrap'>
            <table className='bc_sh_table'>
              <thead><tr><th scope='col'>{hierarchyColumnLabel(tableLevel, daily)}</th><th scope='col' className='is-number'>Doanh thu bán hàng</th><th scope='col' className='is-number'>Giảm trừ</th><th scope='col' className='is-number'>Doanh thu thuần (chưa VAT)</th></tr></thead>
              <tbody>
                <tr className='bc_sh_total'><th scope='row'><strong>Tổng cộng</strong><small>{scopeCount}</small></th><td className='is-number'>{formatHierarchyNumber(summary.gross_revenue_exact ?? summary.gross_revenue)}</td><td className='is-number'>{formatHierarchyNumber(summary.return_amount_exact ?? summary.return_amount)}</td><td className='is-number is-net'>{formatHierarchyNumber(summary.net_revenue_exact ?? summary.net_revenue)}</td></tr>
                {rows.map((row) => <tr key={row.id || row.date}>
                  <th scope='row'>{daily || data.level === 'store' ? formatHierarchyDate(row.date) : <><button type='button' className='bc_sh_row_link' disabled={row.is_unallocated} onClick={() => openRow(row)}><span>{row.label}</span>{!row.is_unallocated ? <FiArrowRight aria-hidden='true' /> : null}</button>{allStores && !branchId ? <small>{row.business_name}</small> : null}</>}</th>
                  <td className='is-number'>{formatHierarchyNumber(row.gross_revenue_exact ?? row.gross_revenue)}</td><td className={`is-number${Number(row.return_amount) < 0 ? ' is-return' : ''}`}>{formatHierarchyNumber(row.return_amount_exact ?? row.return_amount)}</td><td className='is-number is-net'>{formatHierarchyNumber(row.net_revenue_exact ?? row.net_revenue)}</td>
                </tr>)}
                {!rows.length ? <tr><td colSpan='4' className='bc_sh_empty'>Không có dữ liệu bán hàng trong phạm vi và khoảng ngày đã chọn.</td></tr> : null}
              </tbody>
            </table>
          </div>
          <footer className='bc_sh_report_footer'><span>Doanh thu thuần = Doanh thu bán hàng + Giảm trừ · Chưa VAT · Ngày thực xuất kho / nhập trả</span><span>Đơn vị: VND</span></footer>
        </section>
      ) : null}
    </ReportPageLayout>
  );
};

export default SalesHierarchyReportPage;
